import { setErrorCode, setLastSearchedInput, setLoading, type JourneyLeg } from '@/src/redux/slices/aiRecommendationSlice'
import { useAppDispatch } from '@/src/redux/store'
import type { AIRecommendationSuggestionsRequest, AIRecommendationSuggestionsResponse } from '@/src/types/aiRecommendation'
import type { SmartSRPErrorCode } from '@/src/utils/srp/aiRecommendation/locale'
import { useCallback } from 'react'
import useSearchResults from '../useSearchResults'

export function useFetchAISuggestions () {
    const dispatch = useAppDispatch()
    const { selectedJourneyLeg } = useSearchResults()

    const fetchSuggestions = useCallback(
        async (naturalLanguageInput: string) => {
            const leg = selectedJourneyLeg as JourneyLeg
            const trimmedInput = naturalLanguageInput.trim()
            dispatch(setLoading({ leg, value: true }))
            const body: AIRecommendationSuggestionsRequest = {
                naturalLanguageInput: trimmedInput || undefined
            }
            try {
                const res = await fetch('/api/ai-recommendation', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                })
                const data = (await res.json()) as AIRecommendationSuggestionsResponse | { errorCode?: SmartSRPErrorCode }
                if (!res.ok) {
                    const code = (data as { errorCode?: SmartSRPErrorCode }).errorCode ?? 'SOMETHING_WENT_WRONG'
                    console.error('[AI Recommendation] API error:', res.status, data)
                    dispatch(setErrorCode({ leg, value: code }))
                    dispatch(setLoading({ leg, value: false }))
                    return null
                }
                dispatch(setLastSearchedInput({ leg, value: trimmedInput }))
                return data as AIRecommendationSuggestionsResponse
            } catch (e) {
                console.error('[AI Recommendation] Network error:', e instanceof Error ? e.message : e)
                dispatch(setErrorCode({ leg, value: 'NETWORK_ERROR' }))
                return null
            } finally {
                dispatch(setLoading({ leg, value: false }))
            }
        },
        [dispatch, selectedJourneyLeg]
    )

    return { fetchSuggestions }
}
